import React from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

// global
import { Flex } from 'shared/ui'
import { runOnNextTick } from 'util/functions'
import { isCmdEnter } from 'util/keys'
import { ENTER, TAB } from 'constants/keys'

// re-used
import NavBar from 'components/App/DesktopView/shared/Composer/Email/Header/NavBar'
import EmojiPicker from 'components/App/DesktopView/shared/EmojiPicker'
import ToggleKbSearch from 'ducks/ticketing/components/ToggleKbSearch'
import AssignmentBadge from 'components/App/DesktopView/shared/AssignmentDropdown/Badge'
import ReplyStateIndicator from 'components/ReplyStateIndicator'
import Attachments from 'components/Attachments'
import EditorDropzoneIndicator from 'components/App/DesktopView/shared/Composer/shared/EditorDropzoneIndicator'
import withDropzone from 'components/Dropzone'
import MentionSelector from 'components/MentionSelector'

// styles
import styles from 'components/App/DesktopView/CommunicationForms/Reply/shared/styles.less'

// local
import Button from 'components/App/DesktopView/CommunicationForms/Reply/shared/SendButton'
import AssignmentDropdown from 'components/App/DesktopView/CommunicationForms/Reply/shared/AssignmentDropdown'
import DeleteDraftButton from 'components/App/DesktopView/CommunicationForms/Reply/shared/DeleteDraftButton'
import AttachmentButton from 'components/App/DesktopView/CommunicationForms/Reply/shared/AttachmentButton'
import Editor from './EditorView'

const FlexWithDropzone = withDropzone(Flex, {
  indicatorComponent: EditorDropzoneIndicator,
  className: cn(styles.EditorDropzone, 'Dropzone'),
})

const FooterWithDropzone = withDropzone('div', {
  skipIndicator: true,
})

class NoteForm extends React.PureComponent {
  static propTypes = {
    draftId: PropTypes.string.isRequired,
  }

  state = {
    expandedVisible: this.props.initialExpandedVisible || false,
  }

  // We need to do this, because we cannot hide the editor until changesets are fully loaded, so it is possible to start a draft _without_ defaults. Which is why we need to detect the moment the defaults are available and use doEnsureDefaults to safely set them.
  componentDidUpdate() {
    const {
      defaultsAvailable,
      draftId,
      defaultsSet,
      doEnsureDefaults,
    } = this.props
    if (draftId && !defaultsSet && defaultsAvailable) {
      runOnNextTick(() => doEnsureDefaults(draftId))
    }
  }

  onEditorContentChange = text => {
    const { debouncedHandleDraftChange, draftId, ticketId } = this.props

    if (!draftId && !text) return

    debouncedHandleDraftChange(ticketId, 'body', text, {
      draftId,
    })
  }

  onAgentChange = (_, groupId, agentId) => {
    const { handleDraftChange, draftId, ticketId } = this.props
    handleDraftChange(ticketId, 'assigneeId', agentId, {
      draftId,
    })
  }

  onGroupChange = (_, groupId) => {
    const { handleDraftChange, draftId, ticketId } = this.props
    handleDraftChange(ticketId, 'assigneeGroupId', groupId, {
      draftId,
    })
  }

  onAttachmentClick = () => {
    const { handleDraftChange, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'touched', true, {
      draftId,
    })
  }

  onSend = () => {
    const { sendDraft, draftId } = this.props

    sendDraft(draftId)
  }

  onSendAndOpen = () => {
    const { handleDraftChange, sendDraft, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'state', 'opened', { draftId })
    sendDraft(draftId)
  }

  onSendAndClose = () => {
    const { handleDraftChange, sendDraft, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'state', 'closed', { draftId })
    sendDraft(draftId)
  }

  onSendAndSnooze = snoozeUntil => {
    const { handleDraftChange, sendDraft, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'snoozeUntil', snoozeUntil, { draftId })
    sendDraft(draftId)
  }

  onDeleteClick = () => {
    const { deleteDraft, ticketId, draftId } = this.props
    deleteDraft(ticketId, draftId)
  }

  afterAttachmentDelete = () => {
    const { handleDraftChange, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'touched', true, {
      draftId,
    })
  }

  handleDropOnEditor = accepted => {
    const { uploadFiles, draftId } = this.props
    if (!draftId) this.onAttachmentClick()
    runOnNextTick(() => {
      const updatedDraftId = this.props.draftId
      uploadFiles(
        accepted,
        false, // forNote,
        updatedDraftId
      )
    })
  }

  handleDropOnFooter = accepted => {
    const { uploadFiles, draftId } = this.props
    if (!draftId) this.onAttachmentClick()
    setTimeout(() => {
      const updatedDraftId = this.props.draftId
      uploadFiles(
        accepted,
        false, // forNote,
        updatedDraftId
      )
    }, 100)
  }

  startTyping = () => {
    const {
      ticketId,
      doRealtimeAgentStartTicketTypingNotification,
    } = this.props
    doRealtimeAgentStartTicketTypingNotification(ticketId)
  }

  handleOnKeyDownEvent = e => {
    // do not re-do the whole mention UI, we're going to dispatch a keydown event
    // so that the Mentions dropdown will capture it and behave like it did before
    // it's needed because tinymce events happen within an iframe
    const event = document.createEvent('Event')
    const type = e.type

    // can bubble, and is cancellable
    event.initEvent(`editor-${type}`, true, true)
    event.forwarded = e
    document.dispatchEvent(event)

    if ((e.keyCode === TAB || e.keyCode === ENTER) && this.state.word) {
      const {
        doMentionAgent,
        selectedMentionSelectorAgentId,
        suppressMentionSelector,
      } = this.props

      if (!suppressMentionSelector) {
        doMentionAgent(selectedMentionSelectorAgentId, ' ')
        e.preventDefault()
        e.stopPropagation()
      }
    }
    if (isCmdEnter(e)) {
      e.preventDefault()
      e.stopPropagation()
      return this.onSend()
    }
    this.startTyping()

    return true
  }

  handleInput = mention => {
    const { doUpdateAgentMention } = this.props
    const found = mention

    if (!found) {
      this.setState({ word: null })
      return this.closeMentionModalUnlessClosed()
    }

    const word = found.word
    this.setState({ word: found })

    if (word.charAt(0) === '@') return doUpdateAgentMention(found)

    return this.closeMentionModalUnlessClosed()
  }

  closeMentionModalUnlessClosed = () => {
    if (!this.props.isMentioning) return
    this.props.doUpdateAgentMention(null)
  }

  render() {
    const {
      ticketId,
      draftId,
      assignmentLabel,
      assigneeGroupId,
      isSendable,
      isOpen,
      showMentionSelector,
      onEditorFormFocus,
      onEditorFormBlur,
      hideForward,
    } = this.props
    return (
      <div className={styles.form} data-test-id="reply-editor-note">
        <div className="mention-wrapper" key="mentionwrapper">
          {showMentionSelector && <MentionSelector />}
        </div>
        <div
          className={cn('inner', {
            note: true,
          })}
        >
          <div className="header">
            <div className="top-section">
              <NavBar
                ticketId={ticketId}
                onExpandClick={this.onExpandedHeaderOpen}
                onReplyClick={this.onReplyClick}
                onForwardClick={this.onForwardClick}
                forwarding={false}
                commenting
                hideForward={hideForward}
              />
            </div>
          </div>
          <FlexWithDropzone
            className={cn('editorAndAttachments grui flex-2-1-auto flex-col')}
            onDropCustom={this.handleDropOnEditor}
          >
            <Editor
              className={'toolbar-bottom'}
              ticketId={ticketId}
              draftId={draftId}
              onChange={this.onEditorContentChange}
              onKeyDown={this.handleOnKeyDownEvent}
              onMention={this.handleInput}
              onFocus={onEditorFormFocus}
              onBlur={onEditorFormBlur}
            />

            <div id="email-editor-toolbar" />
            <div className="attachments">
              <Attachments
                forDraft
                draftId={draftId}
                ticketId={ticketId}
                afterDelete={this.afterAttachmentDelete}
              />
            </div>
          </FlexWithDropzone>
          <div className="divider" />
          <FooterWithDropzone
            className="footer"
            onDropCustom={this.handleDropOnFooter}
          >
            <div className="left-side actions">
              <div className="attachments">
                <AttachmentButton
                  onClick={this.onAttachmentClick}
                  draftId={draftId}
                />
              </div>
              <DeleteDraftButton
                draftId={draftId}
                onClick={this.onDeleteClick}
              />
              <EmojiPicker direction="right" />
              <ToggleKbSearch className="kb-search" />
            </div>
            <div className="right-side">
              <AssignmentDropdown
                direction="left"
                drilledDownGroupId={assigneeGroupId}
                key="assignment"
                onGroupSelect={this.onGroupChange}
                onAgentSelect={this.onAgentChange}
                trigger={
                  <div>
                    <AssignmentBadge
                      assignmentLabel={assignmentLabel}
                      size="small"
                      iconSize={24}
                      tooltipPosition="top"
                    />
                  </div>
                }
                upward
              />

              <div className="sendButtonContainer">
                <Button
                  className="note-button"
                  actionLabel={'Add note'}
                  disabled={!isSendable}
                  isOpen={isOpen}
                  isNote
                  onSendClick={this.onSend}
                  onSendOpenClick={this.onSendAndOpen}
                  onSendClosedClick={this.onSendAndClose}
                  onSnoozeOptionClick={this.onSendAndSnooze}
                />
                <ReplyStateIndicator
                  className={'collisionIndicator'}
                  hideDraftIndicator
                  conversationId={ticketId}
                />
              </div>
            </div>
          </FooterWithDropzone>
        </div>
      </div>
    )
  }
}

export default NoteForm
